/**
 * `/meetings/:id` — the transcript, the summary, the highlights and the voices.
 *
 * The FOLLOWUPS entry this closes read: "Web `/meetings/:id` transcript page is
 * not implemented." Until now the timeline's meeting row said so in italics.
 *
 * ## Layout, and why it is this way round
 *
 * The summary is what a person came for; the transcript is the evidence behind
 * it. So the summary and the highlights lead, the transcript is the long column
 * underneath, and every highlight that cites a segment index links INTO it —
 * §1.9's hard check ("every action item traces to a segment") is only worth
 * enforcing if the citation is reachable.
 *
 * ## Four states this page has to render honestly
 *
 *  - **Still uploading.** No transcript row yet. Not an error.
 *  - **Refused.** `failureReason` starts with `silent_tap`: the recording was
 *    silence, the files were kept for diagnosis, and no transcript will come.
 *    That is a specific message, not "something went wrong".
 *  - **Transcribed, not summarised.** The digest was dropped or the model is not
 *    configured. The transcript is real and readable; saying "no summary" beats
 *    an empty page.
 *  - **Complete.**
 */

import {
  Badge,
  Button,
  EmptyState,
  SkeletonRows,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@crm/ui';
import { createFileRoute } from '@tanstack/react-router';
import { AlertTriangleIcon, RefreshCwIcon, SparklesIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  useAssignSpeakerMutation,
  useMeetingHighlightsQuery,
  useMeetingSpeakersQuery,
  useMeetingSummaryQuery,
  useMeetingTranscriptQuery,
  useRegenerateMeetingSummaryMutation,
} from '../../../api/queries.ts';
import { SpeakerPanel } from '../../../components/meetings/speaker-panel.tsx';
import { PageBody, PageHeader } from '../../../components/page.tsx';
import { RouteErrorPanel } from '../../../components/route-error-boundary.tsx';
import { formatDateTime } from '../../../lib/format.ts';

export const Route = createFileRoute('/_authed/meetings/$meetingId')({
  component: MeetingPage,
  errorComponent: RouteErrorPanel,
});

function timecode(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

const HIGHLIGHT_TONE: Readonly<Record<string, 'default' | 'muted' | 'warning' | 'destructive'>> = {
  decision: 'default',
  action_item: 'default',
  risk: 'destructive',
  question: 'warning',
  key_point: 'muted',
  quote: 'muted',
};

function MeetingPage() {
  const { meetingId } = Route.useParams();
  const transcript = useMeetingTranscriptQuery(meetingId);
  const summary = useMeetingSummaryQuery(meetingId);
  const highlights = useMeetingHighlightsQuery(meetingId);
  const speakers = useMeetingSpeakersQuery(meetingId);
  const assign = useAssignSpeakerMutation(meetingId);
  const regenerate = useRegenerateMeetingSummaryMutation(meetingId);
  const [focusedSegment, setFocusedSegment] = useState<number | null>(null);

  const pages = transcript.data?.pages ?? [];
  const meeting = pages[0]?.meeting ?? summary.data?.meeting ?? null;
  const segments = useMemo(() => pages.flatMap((page) => page.segments), [pages]);

  /** Label -> the name someone or something gave it. */
  const speakerNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const speaker of speakers.data?.items ?? []) {
      const name =
        speaker.track === 'mic'
          ? 'You'
          : (speakers.data?.candidates.find(
              (candidate) => candidate.contactId === speaker.resolvedContactId,
            )?.name ??
            speaker.displayName ??
            null);
      if (name !== null) map.set(`${speaker.track} ${speaker.speakerLabel}`, name);
    }
    return map;
  }, [speakers.data]);

  if (transcript.isLoading && meeting === null) {
    return <SkeletonRows rows={6} className="p-4" />;
  }
  if (meeting === null) {
    return <EmptyState kind="error" className="m-4" title="Could not load this meeting" />;
  }

  const refused = (meeting.failureReason ?? '').startsWith('silent_tap');
  const transcriptRow = pages[0]?.transcript ?? null;
  const summaryRow = summary.data?.summary ?? null;

  return (
    <>
      <PageHeader
        title={meeting.title ?? 'Meeting'}
        description={[
          formatDateTime(meeting.startedAt),
          meeting.durationSeconds === null
            ? null
            : `${Math.round(meeting.durationSeconds / 60)} min`,
          transcriptRow === null
            ? null
            : `${transcriptRow.segmentCount ?? segments.length} segments`,
        ]
          .filter((part): part is string => part !== null && part !== '')
          .join(' · ')}
        actions={
          <>
            <Badge variant={meeting.status === 'complete' ? 'success' : 'muted'}>
              {meeting.status}
            </Badge>
            {transcriptRow?.isFinal === true ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => regenerate.mutate()}
                disabled={regenerate.isPending}
              >
                <RefreshCwIcon aria-hidden className="size-3.5" />
                Regenerate
              </Button>
            ) : null}
          </>
        }
      />

      <PageBody>
        {refused ? (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
            <AlertTriangleIcon aria-hidden className="mt-0.5 size-4 shrink-0 text-destructive" />
            <div>
              <p className="font-medium">This recording was refused.</p>
              <p className="mt-1 text-muted-foreground">
                One of the tracks recorded pure silence (RMS 0). That is almost always an unsigned
                build or a mis-built aggregate device rather than a quiet room, so no transcript was
                accepted. The audio files were kept on the Mac for diagnosis.
              </p>
            </div>
          </div>
        ) : null}

        {regenerate.data !== undefined && !regenerate.data.queued ? (
          <p className="text-sm text-muted-foreground">
            Not queued: {regenerate.data.reason ?? 'a run is already in flight'}.
          </p>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <section className="flex min-w-0 flex-col gap-4">
            <SummaryCard
              summaryMd={summaryRow?.summaryMd ?? null}
              modelId={summaryRow?.modelId ?? null}
              hasTranscript={transcriptRow !== null}
              refused={refused}
            />

            <section aria-labelledby="transcript-heading" className="min-w-0">
              <h2 id="transcript-heading" className="mb-2 text-sm font-semibold">
                Transcript
              </h2>
              {transcriptRow === null ? (
                <p className="text-sm text-muted-foreground">
                  {refused
                    ? 'No transcript was accepted for this recording.'
                    : 'The recorder has not finished uploading this meeting yet.'}
                </p>
              ) : (
                <ol className="flex flex-col gap-1">
                  {segments.map((segment) => (
                    <li
                      key={segment.id}
                      id={`segment-${segment.idx}`}
                      className={
                        focusedSegment === segment.idx
                          ? 'rounded-md bg-primary/10 px-2 py-1'
                          : 'px-2 py-1'
                      }
                    >
                      <span className="mr-2 font-mono text-xs text-muted-foreground">
                        {timecode(segment.startMs)}
                      </span>
                      <span className="mr-2 text-xs font-medium">
                        {speakerNames.get(`${segment.track} ${segment.speakerLabel ?? ''}`) ??
                          (segment.track === 'mic' ? 'You' : (segment.speakerLabel ?? 'Unknown'))}
                      </span>
                      <span className="text-sm">{segment.text}</span>
                    </li>
                  ))}
                </ol>
              )}
              {transcript.hasNextPage ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => void transcript.fetchNextPage()}
                  disabled={transcript.isFetchingNextPage}
                >
                  Load more
                </Button>
              ) : null}
            </section>
          </section>

          <aside className="flex min-w-0 flex-col gap-4">
            <Tabs defaultValue="highlights">
              <TabsList>
                <TabsTrigger value="highlights">Highlights</TabsTrigger>
                <TabsTrigger value="speakers">Speakers</TabsTrigger>
              </TabsList>

              <TabsContent value="highlights">
                {(highlights.data?.items ?? []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No highlights yet. They land with the summary.
                  </p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {(highlights.data?.items ?? []).map((highlight) => (
                      <li key={highlight.id} className="rounded-md border border-border p-2">
                        <Badge variant={HIGHLIGHT_TONE[highlight.kind] ?? 'muted'}>
                          {highlight.kind.replace(/_/g, ' ')}
                        </Badge>
                        <p className="mt-1 text-sm">{highlight.text}</p>
                        {highlight.segmentIdx === null ? null : (
                          <button
                            type="button"
                            className="mt-1 text-xs text-primary underline-offset-2 hover:underline"
                            onClick={() => {
                              // The citation §1.9 asks the model to produce is
                              // only worth enforcing if it is reachable.
                              setFocusedSegment(highlight.segmentIdx);
                              document
                                .getElementById(`segment-${highlight.segmentIdx}`)
                                ?.scrollIntoView({ block: 'center' });
                            }}
                          >
                            Jump to the transcript
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </TabsContent>

              <TabsContent value="speakers">
                <SpeakerPanel
                  speakers={speakers.data?.items ?? []}
                  candidates={speakers.data?.candidates ?? []}
                  isAssigning={assign.isPending}
                  onAssign={(input) => assign.mutate(input)}
                />
              </TabsContent>
            </Tabs>
          </aside>
        </div>
      </PageBody>
    </>
  );
}

/**
 * The summary, and the three honest empty states around it.
 *
 * Markdown is rendered as paragraphs and `##` headings rather than through a
 * markdown library: this app ships no markdown renderer, the model's output is
 * constrained to short paragraphs and `##` sections by its own prompt, and
 * `dangerouslySetInnerHTML` over model output is exactly the thing not to do.
 */
function SummaryCard({
  summaryMd,
  modelId,
  hasTranscript,
  refused,
}: {
  summaryMd: string | null;
  modelId: string | null;
  hasTranscript: boolean;
  refused: boolean;
}) {
  if (summaryMd === null) {
    return (
      <p className="text-sm text-muted-foreground">
        {refused
          ? 'No summary: the recording was refused.'
          : hasTranscript
            ? 'No summary yet. The transcript is above; a summary appears once the pipeline runs, or if the AI layer is configured.'
            : 'No summary yet — the recording is still uploading.'}
      </p>
    );
  }

  return (
    <section aria-labelledby="summary-heading">
      <div className="mb-2 flex items-center gap-2">
        <h2 id="summary-heading" className="text-sm font-semibold">
          Summary
        </h2>
        {/* Model output is NEVER presented as user-authored fact. */}
        <Badge variant="muted" className="gap-1" title={modelId ?? undefined}>
          <SparklesIcon aria-hidden className="size-3" />
          written by {shortModel(modelId)}
        </Badge>
      </div>
      <div className="flex flex-col gap-2">
        {summaryMd.split('\n').map((line, index) => {
          const trimmed = line.trim();
          if (trimmed === '') return null;
          const key = `${index}-${trimmed.slice(0, 24)}`;
          if (trimmed.startsWith('## ')) {
            return (
              <h3 key={key} className="mt-1 text-sm font-semibold">
                {trimmed.slice(3)}
              </h3>
            );
          }
          return (
            <p key={key} className="text-sm">
              {trimmed.replace(/^[-*]\s+/, '• ')}
            </p>
          );
        })}
      </div>
    </section>
  );
}

function shortModel(modelId: string | null): string {
  if (modelId === null) return 'the model';
  const parts = modelId.split('/');
  return parts[parts.length - 1] ?? modelId;
}
